import {
	ChevronDownIcon,
	ChevronRightIcon,
	ChevronUpIcon,
	PlayIcon,
} from "@heroicons/react/24/outline";
import { type CSSProperties, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./AdditionalInfoSection.module.css";

interface AdditionalInfoSectionProps {
	/** i18n key prefix under taskCatalogDetail (e.g. "book_appointment") */
	taskKey:
		| "book_appointment"
		| "upload_documents"
		| "sign_consent"
		| "fill_health_questionnaire";
	/** Hex color for the colored top block and bullet accents */
	domainColor: string;
	/** Optional deadline string displayed in the summary card */
	deadline?: string;
}

export function AdditionalInfoSection({
	taskKey,
	domainColor,
	deadline,
}: AdditionalInfoSectionProps) {
	const { t } = useClientTranslations("getReady");
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(false);

	const taskTitle = t(`taskCatalog.${taskKey}.title`);
	const description = t(`taskCatalogDetail.${taskKey}.description`);
	const prepTipsRaw = t(`taskCatalogDetail.${taskKey}.prepTips`, {
		returnObjects: true,
	});
	const prepTips: string[] = Array.isArray(prepTipsRaw)
		? prepTipsRaw.filter((x): x is string => typeof x === "string")
		: [];

	const eduTitle = t("additionalInfo.educationHub.title");
	const eduSubtext = t("additionalInfo.educationHub.subtext");
	const label = t("additionalInfo.label");
	const aboutLabel = t("additionalInfo.aboutLabel");

	return (
		<div className={styles.container}>
			<button
				type="button"
				className={`${styles.toggleRow}${expanded ? ` ${styles.toggleRowExpanded}` : ""}`}
				onClick={() => setExpanded((v) => !v)}
				aria-expanded={expanded}
			>
				<span className={styles.toggleLabel}>{label}</span>
				{expanded ? (
					<ChevronUpIcon className={styles.chevronIcon} aria-hidden="true" />
				) : (
					<ChevronDownIcon className={styles.chevronIcon} aria-hidden="true" />
				)}
			</button>

			{expanded && (
				<div className={styles.content}>
					{/* Colored summary card with icon + title + deadline */}
					<section
						className={styles.summaryCard}
						style={{ "--domain-color": domainColor } as CSSProperties}
					>
						<div className={styles.summaryHeader}>
							<span className={styles.iconCircle} aria-hidden="true">
								<PlayIcon className={styles.iconGlyph} aria-hidden="true" />
							</span>
							<div className={styles.summaryTextStack}>
								<h3 className={styles.summaryTitle}>{taskTitle}</h3>
								{deadline && <p className={styles.deadlineText}>{deadline}</p>}
							</div>
						</div>
					</section>

					{/* About description */}
					<section className={styles.aboutBlock}>
						<h3 className={styles.sectionTitle}>{aboutLabel}</h3>
						<p className={styles.aboutBody}>{description}</p>
					</section>

					{/* Preparation tips */}
					{prepTips.length > 0 && (
						<section className={styles.tipsBlock}>
							<h3 className={styles.sectionTitle}>
								{t("additionalInfo.prepTipsTitle")}
							</h3>
							<ul className={styles.tipsList}>
								{prepTips.map((tip) => (
									<li key={tip} className={styles.tipItem}>
										<span
											className={styles.tipBullet}
											style={{ "--domain-color": domainColor } as CSSProperties}
											aria-hidden="true"
										/>
										<span className={styles.tipText}>{tip}</span>
									</li>
								))}
							</ul>
						</section>
					)}

					{/* Education hub card */}
					<button
						type="button"
						className={styles.eduCard}
						style={{ "--domain-color": domainColor } as CSSProperties}
						onClick={() => navigate("/education")}
						aria-label={eduTitle}
					>
						<span className={styles.eduIconCircle} aria-hidden="true">
							<PlayIcon className={styles.eduIcon} aria-hidden="true" />
						</span>
						<span className={styles.eduText}>
							<span className={styles.sectionTitle}>{eduTitle}</span>
							<span className={styles.eduSubtext}>{eduSubtext}</span>
						</span>
						<ChevronRightIcon
							className={styles.eduChevron}
							aria-hidden="true"
						/>
					</button>
				</div>
			)}
		</div>
	);
}
